import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../AuthContext";
import { update } from "lodash";

const EmployeeForm = ({ setOpenDepartment, updateDepartments }) => {
  const [roles, setRoles] = useState([]); // Stores roles data
  const [staff, setStaff] = useState([]); // Stores staff data
  const [name, setName] = useState(""); // Department Name field
  const [selectedRoles, setSelectedRoles] = useState([]); // Stores selected roles
  const [manager, setManager] = useState(""); // Selected manager
  const [reportsTo, setReportsTo] = useState(""); // Selected ReportsTo
  const [departments, setDepartments] = useState([]); // List of existing departments
  const [nameError, setNameError] = useState(""); // Error message for Name uniqueness

  const user = useContext(AuthContext);
  
  // Fetch roles and departments from the server
  useEffect(() => {
    const getData = async () => {
      try {
        // Fetch departments
        const responseDepartments = await fetch(
          "https://api.insightos.co.uk/api/getdepartments",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: 'include',

          }
        );
        if (!responseDepartments.ok) {
          throw new Error("Failed to fetch departments");
        }
        const dataDepartments = await responseDepartments.json();
        setDepartments(dataDepartments.data);

        // Fetch roles
        const responseRoles = await fetch("https://api.insightos.co.uk/api/allroles", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        });
        if (!responseRoles.ok) {
          throw new Error("Failed to fetch roles");
        }
        const dataRoles = await responseRoles.json();
        setRoles(dataRoles.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    getData();
  }, [user.companyCode]);

  // Fetch staff from the server and filter based on ManagementLevel
  useEffect(() => {
    const getStaff = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/staff", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error("Failed to fetch staff");
        }
        const data = await response.json();
        const staffData = data.data;
        // Filter staff with ManagementLevel > 1
        const staffWithRole = staffData.filter(
          (person) => person.ManagementLevel > 1
        );
        setStaff(staffWithRole);
      } catch (error) {
        console.error("Error fetching staff:", error);
      }
    };

    getStaff();
  }, [roles, user.companyCode]);

  // Check the uniqueness of the department name as the user types
  useEffect(() => {
    if (name.trim() !== "") {
      const departmentNames = departments.map((dept) => dept.Name.toLowerCase());
      if (departmentNames.includes(name.toLowerCase())) {
        setNameError("Department name already exists. Please choose a unique name.");
      } else {
        setNameError("");
      }
    } else {
      setNameError("");
    }
  }, [name, departments]);

  // Handle multiple roles selection
  const handleRoleChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(
      (option) => option.value
    );
    setSelectedRoles(selectedOptions);
  };

  const handleCancel = () => {
    setOpenDepartment(false);
  };

  // Save the department
  const handleSave = async () => {
    // (The Save button is disabled if there's an error)
    try {
      const response = await fetch("https://api.insightos.co.uk/api/adddepartment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
          Name: name,
          Roles: selectedRoles, // You can convert to a comma-separated string if needed
          Manager: manager,
          ReportsTo: reportsTo,
          CompanyCode: user.companyCode,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to save department");
      }
      setOpenDepartment(false);
      updateDepartments();
    } catch (error) {
      console.error("Error saving department:", error);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      {/* Department Name Input */}
      <div className="mb-4">
        <label htmlFor="name" className="block text-sm font-medium text-gray-900">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Department Name"
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600 sm:text-sm"
        />
        {nameError && (
          <p className="mt-1 text-sm text-red-600">{nameError}</p>
        )}
      </div>

      {/* Roles in Department (Multi-Select) */}
      <div className="mb-4">
        <label htmlFor="roles" className="block text-sm font-medium text-gray-900">
          Roles in Department (click and hold ctrl to select multiple roles)
        </label>
        <select
          id="roles"
          multiple
          size={5}
          value={selectedRoles}
          onChange={handleRoleChange}
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600 sm:text-sm"
        >
          {roles.map((role) => (
            <option key={role.Name} value={role.Name}>
              {role.Name}
            </option>
          ))}
        </select>
      </div>

      {/* Display selected roles underneath */}
      {selectedRoles.length > 0 && (
        <div className="mb-4">
          <h3 className="block text-sm font-medium text-gray-900">Selected Roles:</h3>
          <ul className="mt-2">
            {selectedRoles.map((role, index) => (
              <li key={index} className="text-base text-gray-700">
                {role}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Manager Dropdown */}
      <div className="mb-4">
        <label htmlFor="manager" className="block text-sm font-medium text-gray-900">
          Manager
        </label>
        <select
          id="manager"
          value={manager}
          onChange={(e) => setManager(e.target.value)}
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600 sm:text-sm"
        >
          <option value="">Select a manager</option>
          {staff.map((person) => (
            <option key={person.id} value={person.Name}>
              {person.Name}
            </option>
          ))}
        </select>
      </div>

      {/* Reports To Dropdown */}
      <div className="mb-4">
        <label htmlFor="reportsTo" className="block text-sm font-medium text-gray-900">
          Reports To
        </label>
        <select
          id="reportsTo"
          value={reportsTo}
          onChange={(e) => setReportsTo(e.target.value)}
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600 sm:text-sm"
        >
          <option value="">Select a person to report to</option>
          {staff.map((person) => (
            <option key={person.id} value={person.Name}>
              {person.Name}
            </option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={handleCancel}
          className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!!nameError || name.trim() === ""}
          data-component="department-form"
          data-feature="create_department_post"
          data-time-saved="5"
          className={`bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 ${
            !!nameError || name.trim() === "" ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default EmployeeForm;
